// src/components/UserContractView/components/PendingContractsSection.jsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';

import {
  CheckCircle as SignedIcon,
  Pending as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Launch as LaunchIcon,
  Description as DocumentIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

import { buildPublicLink, copyToClipboard } from './contractUtils';
import { BLITZ_ROOT } from './contractConstants';

const PendingContractsSection = ({ pending, onOpenContract, onQuickPreview, loading }) => {
  const [expanded, setExpanded] = useState(true);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'warning.main' }}>
              <PendingIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">
                Action Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pending.length} {pending.length === 1 ? 'contract needs' : 'contracts need'} your attention
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {pending.length === 0 ? (
            <Box textAlign="center" py={4}>
              <SignedIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                All Caught Up!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No pending contracts require your signature
              </Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {pending.map((item, idx) => {
                if (!item) return null;
                
                let assignmentId, assignment, template, isSimple;
                if (item?.template) {
                  assignmentId = item.id;
                  assignment = item;
                  template = item.template;
                  isSimple = item.is_simple;
                } else {
                  assignmentId = item?.assignment?.id || item?.assignment_id;
                  assignment = item?.assignment;
                  template = assignment?.template || {};
                  isSimple = assignment?.is_simple;
                }

                const publicLink = assignmentId ? buildPublicLink(assignmentId, BLITZ_ROOT) : null;

                return (
                  <React.Fragment key={assignmentId || idx}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      sx={{
                        py: 2,
                        '&:hover': { bgcolor: 'action.hover' },
                        borderLeft: '4px solid',
                        borderLeftColor: 'warning.main',
                        bgcolor: 'warning.lighter'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: isSimple ? 'info.main' : 'primary.main' }}>
                          <DocumentIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {template?.name || 'Contract'}
                            </Typography>
                            <Chip
                              size="small"
                              label={isSimple ? 'Terms' : 'Contract'}
                              color={isSimple ? 'info' : 'primary'}
                              sx={{ height: 20 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            {template?.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {template.description}
                              </Typography>
                            )}
                            {assignment?.assignment_message && (
                              <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                                <Typography variant="caption">
                                  <strong>Note:</strong> {assignment.assignment_message}
                                </Typography>
                              </Alert>
                            )}
                            {assignment?.due_date && (
                              <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                ⏰ Due: {new Date(assignment.due_date).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Box display="flex" gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<LaunchIcon />}
                          onClick={() => onOpenContract(assignmentId)}
                          disabled={!assignmentId}
                        >
                          Open
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DocumentIcon />}
                          onClick={() => onQuickPreview(item, false)}
                          disabled={!assignmentId}
                        >
                          Preview
                        </Button>
                        {publicLink && (
                          <Tooltip title="Copy public link">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(publicLink)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PendingContractsSection;